import mappers from "@common/utils/filters/mappers";
import {
  deviceSourceFilterMap,
  deviceStatusMap,
} from "@common/utils/filters/mappers/devices";
import { toEndOfDay, toStartOfDay } from "@common/utils/localTime.utils";
import {
  canViewDevice,
  canViewLocation,
  canViewMeeting,
  canViewRoom,
  canViewTicket,
  canViewUser,
} from "@common/utils/user";
import { TriggerFilters } from "@services/triggers/types";
import { AuthenticatedUser } from "@services/user/types";
import { TIME_FILTER_SEARCH_KEY as MEETING_TIME_FILTER_SEARCH_KEY } from "@common/utils/filters/mappers/meetings";
import { TIME_FILTER_SEARCH_KEY as TICKET_TIME_FILTER_SEARCH_KEY } from "@common/utils/filters/mappers/tickets";
import {
  roomsStatusMap,
  SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
} from "@common/utils/filters/mappers/rooms";
import {
  LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
  WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
} from "@common/utils/filters/mappers/devices";
import { locationsStatusMap } from "@common/utils/filters/mappers/locations";

export const deviceFields = [
  {
    key: "name",
    label: "Device Name",
    default: true,
  },
  {
    key: "status",
    label: "Status",
  },
  {
    key: "service",
    label: "Monitoring Service",
  },
  {
    key: "source",
    label: "Monitoring Source",
  },
  {
    key: "deviceType",
    label: "Type",
  },
  {
    key: "category",
    label: "Category",
  },
  {
    key: "manufacturer",
    label: "Manufacturer",
  },
  {
    key: "model",
    label: "Model",
  },
  {
    key: "accountId",
    label: "Account",
  },
  {
    key: "room.location.name",
    label: "Location Name",
  },
  {
    key: "softwareVersion",
    label: "Software Version",
  },
  {
    key: "serialNumber",
    label: "Serial Number",
  },
  {
    key: "macAddress",
    label: "MAC Address",
  },
  {
    key: "active",
    label: "Active",
  },
  {
    key: "vip",
    label: "High Importance",
  },
  {
    key: "ipAddress",
    label: "Management Address",
  },
  {
    key: "gatekeeperIp",
    label: "Gatekeeper IP",
  },
  {
    key: "e164",
    label: "E.164",
  },
  {
    key: "sipUri",
    label: "SIP URI",
  },
  {
    key: "room.managed",
    label: "Managed",
  },
  {
    key: "isAggregator",
    label: "Monitoring Proxy",
  },
  {
    key: "ucPlatform",
    label: "UC Platform",
  },
  {
    key: "operatingSystem",
    label: "Operating System",
  },
  {
    key: "details.assetTag",
    label: "Asset Tag",
  },
  {
    key: "provisioned",
    label: "Unprovisioned",
  },
  {
    key: "room.details.contactEmail",
    label: "Room Contact Email",
  },
  {
    key: "room.exchangeId",
    label: "Room Exchange ID",
  },
  {
    key: "room.name",
    label: "Room Name",
  },
  {
    key: "room.details.roomType",
    label: "Room Type",
  },
  {
    key: "room.location.country",
    label: "Country",
  },
  {
    key: "room.location.city",
    label: "City",
  },
  {
    key: "callCapable",
    label: "Call Capable",
  },
  {
    key: "inACall",
    label: "In a Call",
  },
  {
    key: LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
    label: "Lease Expiration",
  },
  {
    key: WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
    label: "Warranty Expiration",
  },
  {
    key: "tags",
    label: "Tags",
  },
];

export const userFields = [
  {
    key: "email",
    label: "Username (Email)",
    default: true,
  },
  {
    key: "firstName",
    label: "First Name",
  },
  {
    key: "lastName",
    label: "Last Name",
  },
  {
    key: "role.id",
    label: "Role",
  },
  {
    key: "author",
    label: "Author",
  },
];

export const meetingFields = [
  {
    key: "status",
    label: "Status",
  },
  {
    key: MEETING_TIME_FILTER_SEARCH_KEY,
    label: "Time",
  },
  {
    key: "assignee",
    label: "Assignee",
  },
  {
    key: "purpose",
    label: "Purpose",
  },
  {
    key: "type",
    label: "Type",
  },
  {
    key: "scheduledMeeting.producerService",
    label: "Producer Attendance",
  },
  {
    key: "vip",
    label: "VIP",
  },
  {
    key: "accountId",
    label: "Account",
  },
  {
    key: "participants.room.location.name",
    label: "Location",
  },
  {
    key: "participants.room.name",
    label: "Room",
  },
  {
    key: "includeAllWithOpenTickets",
    label: "Tickets",
  },
  {
    key: "scheduledMeeting.meetingSource",
    label: "Source",
  },
  {
    key: "meetingId",
    label: "ID",
    default: true,
  },
  {
    key: "title",
    label: "Title",
  },
  {
    key: "scheduledMeeting.emailParticipants.email",
    label: "Participants",
  },
  {
    key: "scheduledMeeting.requestorEmail",
    label: "Requester",
  },
  {
    key: "scheduledMeeting.evcsMeetingId",
    label: "EVCS ID",
  },
  {
    key: "scheduledMeeting.ownerEmail",
    label: "Owner",
  },
  {
    key: "wrappedUp",
    label: "Wrapped Up",
  },
];

export const ticketFields = [
  {
    key: TICKET_TIME_FILTER_SEARCH_KEY,
    label: "Time",
  },
  {
    key: "status",
    label: "Status",
  },
  {
    key: "priority",
    label: "Priority",
  },
  {
    key: "accountId",
    label: "Account",
  },
  {
    key: "room.location.name",
    label: "Location",
  },
  {
    key: "room.name",
    label: "Room",
  },
  {
    key: "issue.issue",
    label: "Issue Type",
  },
  {
    key: "assignedTo",
    label: "Assignee",
  },
  {
    key: "purpose",
    label: "Issues",
  },
  {
    key: "id",
    label: "ID",
    default: true,
  },
  {
    key: "room.location.country",
    label: "Country",
  },
  {
    key: "device.name",
    label: "Device",
  },
  {
    key: "subject",
    label: "Subject",
  },
  {
    key: "details.requester",
    label: "Opened By",
  },
  {
    key: "meetings.confirmationNumber",
    label: "Meeting ID",
  },
  {
    key: "syncStatus",
    label: "Sync Status",
  },
  {
    key: "talState",
    label: "TAL State",
  },
  {
    key: "talCaseNo",
    label: "TAL Case Number",
  },
  { key: "device.type", label: "Device Type", filterDefKey: "deviceType" },
  {
    key: "device.category",
    label: "Device Category",
    filterDefKey: "category",
  },
  {
    key: "device.manufacturer",
    label: "Device Manufacturer",
    filterDefKey: "manufacturer",
  },
  { key: "device.model", label: "Device Model", filterDefKey: "model" },
];

export const accountFields = [
  {
    key: "active",
    label: "Active",
  },
  {
    key: "consumption",
    label: "Consumption",
  },
  {
    key: "cloudConnector",
    label: "Cloud Connector",
  },
  {
    key: "tags",
    label: "Tags",
  },
];

export const roomFields = [
  {
    key: "accountId",
    label: "Account",
  },
  {
    key: "name",
    label: "Room Name",
    default: true,
  },
  {
    key: "status",
    label: "Device Status",
  },
  {
    key: "vip",
    label: "High Importance",
  },
  {
    key: "details.publicRoom",
    label: "Public Room",
  },
  {
    key: "inService",
    label: "Active",
  },
  {
    key: "managed",
    label: "Managed",
  },
  {
    key: "details.contactName",
    label: "Room Contact Name",
  },
  {
    key: "details.contactEmail",
    label: "Room Contact Email",
  },
  {
    key: "details.certificationStatus",
    label: "Certified",
  },
  {
    key: "details.roomType",
    label: "Room Type",
  },
  {
    key: "details.roomNumber",
    label: "Room Number",
  },
  {
    key: "location.name",
    label: "Location Name",
  },
  {
    key: "location.country",
    label: "Country",
  },
  {
    key: "exchangeId",
    label: "Room Exchange ID",
  },
  {
    key: SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
    label: "Room Service Contract Expiration",
  },
  {
    key: "details.certifiedBy",
    label: "Certified By",
  },
  {
    key: "location.open",
    label: "Location Open Status",
  },
  {
    key: "tags",
    label: "Tags",
  },
];
export const countryFields = [{ key: "status", label: "Status" }];
export const locationFields = [
  { key: "name", label: "Location Name", default: true },
  { key: "contactEmail", label: "Location Contact Email" },
  { key: "contactName", label: "Location Contact Name" },
  { key: "open", label: "Location Open Status" },
  {
    key: "status",
    label: "Device Status",
  },
  {
    key: "active",
    label: "Active",
  },
  {
    key: "country",
    label: "Country",
    filterDefKey: "location.country",
  },
  {
    key: "city",
    label: "City",
  },
  {
    key: "tags",
    label: "Tags",
  },
];
export const triggerFields: Array<{
  key: keyof TriggerFilters;
  label: string;
  filterDefKey?: string;
}> = [
  {
    key: "threshold.deviceType",
    label: "Device Type",
    filterDefKey: "deviceType",
  },
  {
    key: "threshold.category",
    label: "Device Category",
    filterDefKey: "category",
  },
  {
    key: "threshold.manufacturer",
    label: "Device Manufacturer",
    filterDefKey: "manufacturer",
  },
  {
    key: "threshold.deviceModel",
    label: "Device Model",
    filterDefKey: "model",
  },
  {
    key: "accountId",
    label: "Account",
  },
  {
    key: "threshold.priority",
    label: "Ticket Priority",
  },
  {
    key: "hideNotApplicable",
    label: "Applicable Only",
    filterDefKey: "applicableOnly",
  },
  {
    key: "threshold.active",
    label: "Active Only",
    filterDefKey: "active",
  },
  {
    key: "threshold.workflow",
    label: "Workflow",
    filterDefKey: "workflow",
  },
];
export const auditFields = [
  {
    key: "entityType",
    label: "Entity Type",
    filterDefKey: "type",
  },
  {
    key: "userName",
    label: "Username",
    filterDefKey: "email",
  },
  {
    key: "accountId",
    label: "Account",
  },
  {
    key: "action",
    label: "Action",
  },
  {
    key: "time",
    label: "Time",
  },
];
export const workflowFields = [
  {
    key: "type",
    label: "Category",
  },
  {
    key: "active",
    label: "Active",
  },
  {
    key: "accountId",
    label: "Account",
  },
];

export const catalogFields = [
  // !will be uncommented when the BE has added the “active” filter
  // {
  //   key: "active",
  //   label: "Active",
  // },
  {
    key: "deviceType",
    label: "Type",
  },
  {
    key: "category",
    label: "Category",
  },
  {
    key: "manufacturer",
    label: "Manufacturer",
  },
];

const FiltersTypeImpl: { [k in FiltersType]: k } = {
  Devices: "Devices",
  Rooms: "Rooms",
  Meetings: "Meetings",
  Tickets: "Tickets",
  Accounts: "Accounts",
  Countries: "Countries",
  Locations: "Locations",
  Workflows: "Workflows",
  Users: "Users",
  Triggers: "Triggers",
  AuditTrails: "AuditTrails",
  DeviceCatalog: "DeviceCatalog",
};


export const getUrlConstructor = (type: SearchType) => {
  switch (type) {
    case FiltersTypeImpl.Devices:
      return (filters: any) => {
        let temp = { ...filters };
        if (temp.status && Array.isArray(temp.status)) {
          temp.status.forEach((value: DevicesStatusFilter) => {
            const deviceStatus = deviceStatusMap[value];
            if (deviceStatus !== undefined)
              temp = { ...temp, [deviceStatus.key]: deviceStatus.value };
          });
        }
        delete temp.status;
        if (temp.source && Array.isArray(temp.source)) {
          let sourceFilter: string[] = [];
          temp.source.forEach((value: string) => {
            const sourceValue =
              deviceSourceFilterMap[value as DevicesSourceFilter];
            sourceFilter.push(...sourceValue);
          });
          temp = { ...temp, ["monitoringSources"]: sourceFilter };
        }
        delete temp.source;
        if (temp.service && Array.isArray(temp.service)) {
          temp = { ...temp, ["monitoringLevel"]: temp.service };
        }
        delete temp.service;
        return `/assets/devices?${mappers.Devices.fromSL
          .toSearchParams(temp)
          .toString()}`;
      };
    case FiltersTypeImpl.Tickets:
      return (filters: any) => {
        const temp = { ...filters };
        if (temp.lastUpdatedDate !== undefined) {
          temp["lastUpdatedDate.from"] = toStartOfDay(
            new Date(parseInt(temp.lastUpdatedDate))
          ).getTime();
          temp["lastUpdatedDate.to"] = toEndOfDay(
            new Date(parseInt(temp.lastUpdatedDate))
          ).getTime();
          delete temp.lastUpdatedDate;
        }
        return `/tickets?${mappers.Tickets.fromSL
          .toSearchParams(temp)
          .toString()}`;
      };
    case FiltersTypeImpl.Users:
      return (filters: any) =>
        `/configuration/users?${mappers.Users.fromSL
          .toSearchParams(filters)
          .toString()}`;
    case FiltersTypeImpl.Meetings:
      return (filters: any) =>
        `/meetings?${mappers.Meetings.fromSL
          .toSearchParams(filters)
          .toString()}`;
    case FiltersTypeImpl.Rooms:
      return (filters: any) => {
        let temp = { ...filters };
        if (temp.status && Array.isArray(temp.status)) {
          temp.status.forEach((value: RoomsStatusFilter) => {
            const roomsStatus = roomsStatusMap[value];
            if (roomsStatus !== undefined)
              temp = { ...temp, [roomsStatus.key]: roomsStatus.value };
          });
        }
        delete temp.status;
        return `/assets/rooms?${mappers.Rooms.fromSL
          .toSearchParams(temp)
          .toString()}`;
      };
    case FiltersTypeImpl.Locations:
      return (filters: any) => {
        let temp = { ...filters };
        if (temp.status && Array.isArray(temp.status)) {
          temp.status.forEach((value: LocationsStatusFilter) => {
            const locationsStatus = locationsStatusMap[value];
            if (locationsStatus !== undefined)
              temp = { ...temp, [locationsStatus.key]: locationsStatus.value };
          });
        }
        delete temp.status;
        return `/assets/locations?${mappers.Locations.fromSL
          .toSearchParams(temp)
          .toString()}`;
      };
  }
};

export const allFields: {
  [k in FiltersType]: Array<{
    key: string;
    label: string;
    filterDefKey?: string;
  }>;
} = {
  [FiltersTypeImpl.Users]: userFields,
  [FiltersTypeImpl.Devices]: deviceFields,
  [FiltersTypeImpl.Meetings]: meetingFields,
  [FiltersTypeImpl.Tickets]: ticketFields,
  [FiltersTypeImpl.Accounts]: accountFields,
  [FiltersTypeImpl.Rooms]: roomFields,
  [FiltersTypeImpl.Countries]: countryFields,
  [FiltersTypeImpl.Locations]: locationFields,
  [FiltersTypeImpl.Triggers]: triggerFields,
  [FiltersTypeImpl.AuditTrails]: auditFields,
  [FiltersTypeImpl.Workflows]: workflowFields,
  [FiltersTypeImpl.DeviceCatalog]: catalogFields,
};

export const searchOrder: SearchType[] = [
  "Devices",
  "Users",
  "Meetings",
  "Rooms",
  "Locations",
  "Tickets",
];

export const searchFields: { [k in SearchType]: typeof deviceFields } = {
  [FiltersTypeImpl.Rooms]: roomFields,
  [FiltersTypeImpl.Locations]: locationFields,
  [FiltersTypeImpl.Users]: userFields,
  [FiltersTypeImpl.Devices]: deviceFields,
  [FiltersTypeImpl.Meetings]: meetingFields,
  [FiltersTypeImpl.Tickets]: ticketFields,
};

export const getSearchOrder = (user?: AuthenticatedUser) => {
  let temp: SearchType[] = [];
  searchOrder
    .sort((a, b) => a.localeCompare(b))
    .forEach((v) => {
      switch (v) {
        case FiltersTypeImpl.Devices:
          if (canViewDevice(user)) temp.push(v);
          break;
        case FiltersTypeImpl.Users:
          if (canViewUser(user)) temp.push(v);
          break;
        case FiltersTypeImpl.Meetings:
          if (canViewMeeting(user)) temp.push(v);
          break;
        case FiltersTypeImpl.Tickets:
          if (canViewTicket(user)) temp.push(v);
          break;
        case FiltersTypeImpl.Locations:
          if (canViewLocation(user)) temp.push(v);
          break;
        case FiltersTypeImpl.Rooms:
          if (canViewRoom(user)) temp.push(v);
          break;
        default:
          break;
      }
    });
  return temp;
};
